import React, { useState } from 'react';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { CancelIcon } from '@fluentui/react-icons-mdl2';
import { useTranslation } from 'react-i18next';

import DeleteIcon from '../img/icon-Delete2-white.png';
import DeleteHoverIcon from '../img/icon-Delete2-mouse-over.png';
import GroupCollectionWhiteIcon from '../img/icons/icon-collection-white.png';
import MyCollectionWhiteIcon from '../img/icons/icon-my-collection-white.png';
import RemoveGroupCollectionWhiteIcon from '../img/icons/icon-remove-group-collection-white.png';
import RemoveMyCollectionWhiteIcon from '../img/icons/icon-remove-my-collection-white.png';
import backIcon from '../img/icon-Back.png';
import type { IdigestChatTheme } from '../theme';

const circularButtonStyles = {
  root: {
    width: '30px',
    height: '30px',
    minWidth: '30px',
    minHeight: '30px',
    padding: 0,
    margin: 0,
    borderRadius: '50%',
    backgroundColor: 'transparent',
    fontSize: '16px',
    fontWeight: '600',
    position: 'relative' as const,
    selectors: {
      '&.ms-Button': {
        borderRadius: '50%'
      }
    }
  },
  rootHovered: {
    backgroundColor: 'transparent'
  },
  rootPressed: {
    backgroundColor: 'transparent'
  },
  flexContainer: {
    justifyContent: 'center' as const,
    alignItems: 'center' as const
  }
};

const iconStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
};

export interface IChatRoomHeaderProps {
  title: string;
  /** @deprecated Header chrome follows shell CSS vars; prop kept for callers. */
  theme?: IdigestChatTheme;
  onBack?: () => void;
  /** When false, hide back control (split layout keeps the list visible). Default true. */
  showBack?: boolean;
  isBatchSelectMode?: boolean;
  selectedCount?: number;
  hasOthersMessages?: boolean;
  activeFilter?: 'none' | 'user' | 'group' | 'file';
  currentUserIsLeader?: boolean;
  onExitBatchSelect?: () => void;
  onBatchGroupCollection?: () => void;
  onBatchMyCollection?: () => void;
  onBatchDelete?: () => void;
  onBatchRemoveGroupCollection?: () => void;
  onBatchRemoveMyCollection?: () => void;
  /** Extra actions shown in normal (non-batch) mode, e.g. meeting / manage members */
  normalActions?: React.ReactNode;
  autoDeleteNotice?: string;
}

export function ChatRoomHeader({
  title,
  onBack,
  showBack = true,
  isBatchSelectMode = false,
  selectedCount = 0,
  hasOthersMessages = false,
  activeFilter = 'none',
  currentUserIsLeader = false,
  onExitBatchSelect,
  onBatchGroupCollection,
  onBatchMyCollection,
  onBatchDelete,
  onBatchRemoveGroupCollection,
  onBatchRemoveMyCollection,
  normalActions,
  autoDeleteNotice
}: IChatRoomHeaderProps) {
  const { t } = useTranslation();
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);

  const buttonStyles = circularButtonStyles;

  if (isBatchSelectMode) {
    return (
      <div className='panel-header idigest-chat-header delete-mode-header'>
        <div className='delete-mode-cancel-container'>
          {onExitBatchSelect && (
            <DefaultButton
              title={t('Cancel')}
              ariaLabel={t('Cancel')}
              className='delete-mode-cancel-button'
              styles={{
                root: {
                  backgroundColor: 'transparent',
                  border: 'none',
                  minWidth: 'auto',
                  width: '28px',
                  height: '28px',
                  padding: 0
                },
                rootHovered: {
                  backgroundColor: 'transparent'
                }
              }}
              onClick={onExitBatchSelect}
            >
              <CancelIcon className='delete-mode-cancel-icon' />
            </DefaultButton>
          )}
          <span className='delete-mode-selected-count'>
            {t('GroupScreen.SelectedCount').replace('{{count}}', selectedCount.toString())}
          </span>
        </div>
        <div className='header-actions'>
          {activeFilter === 'user' && onBatchRemoveMyCollection ? (
            <div className='button-with-label'>
              <DefaultButton
                className='circular-button'
                title={t('Chatroom.RemoveMyCollection')}
                ariaLabel={t('Chatroom.RemoveMyCollection')}
                styles={buttonStyles}
                onClick={onBatchRemoveMyCollection}
                disabled={selectedCount === 0}
              >
                <img src={RemoveMyCollectionWhiteIcon} alt='RemoveMyCollection' className='normal' style={iconStyle} />
              </DefaultButton>
              <span style={{ opacity: selectedCount === 0 ? 0.5 : 1 }}>{t('Chatroom.RemoveMyCollection')}</span>
            </div>
          ) : (
            onBatchMyCollection && (
              <div className='button-with-label'>
                <DefaultButton
                  className='circular-button'
                  title={t('Chatroom.MyCollection')}
                  ariaLabel={t('Chatroom.MyCollection')}
                  styles={buttonStyles}
                  onClick={onBatchMyCollection}
                  disabled={selectedCount === 0}
                >
                  <img src={MyCollectionWhiteIcon} alt='MyCollection' className='normal' style={iconStyle} />
                </DefaultButton>
                <span style={{ opacity: selectedCount === 0 ? 0.5 : 1 }}>{t('Chatroom.MyCollectionShort')}</span>
              </div>
            )
          )}
          {activeFilter === 'group' && onBatchRemoveGroupCollection ? (
            <div className='button-with-label'>
              <DefaultButton
                className='circular-button'
                title={t('Chatroom.RemoveGroupCollection')}
                ariaLabel={t('Chatroom.RemoveGroupCollection')}
                styles={buttonStyles}
                onClick={onBatchRemoveGroupCollection}
                disabled={selectedCount === 0 || !currentUserIsLeader}
              >
                <img
                  src={RemoveGroupCollectionWhiteIcon}
                  alt='RemoveGroupCollection'
                  className='normal'
                  style={iconStyle}
                />
              </DefaultButton>
              <span style={{ opacity: selectedCount === 0 || !currentUserIsLeader ? 0.5 : 1 }}>
                {t('Chatroom.RemoveGroupCollection')}
              </span>
            </div>
          ) : (
            onBatchGroupCollection && (
              <div className='button-with-label'>
                <DefaultButton
                  className='circular-button'
                  title={t('Chatroom.GroupCollection')}
                  ariaLabel={t('Chatroom.GroupCollection')}
                  styles={{
                    ...buttonStyles,
                    root: {
                      ...buttonStyles.root,
                      ...((selectedCount === 0 || !currentUserIsLeader) && {
                        opacity: '0.5',
                        selectors: {
                          '&.ms-Button:disabled': {
                            backgroundColor: 'transparent',
                            cursor: 'not-allowed'
                          }
                        }
                      })
                    }
                  }}
                  onClick={onBatchGroupCollection}
                  disabled={selectedCount === 0 || !currentUserIsLeader}
                >
                  <img src={GroupCollectionWhiteIcon} alt='GroupCollection' className='normal' style={iconStyle} />
                </DefaultButton>
                <span style={{ opacity: selectedCount === 0 || !currentUserIsLeader ? 0.5 : 1 }}>
                  {t('Chatroom.GroupCollectionShort')}
                </span>
              </div>
            )
          )}
          {onBatchDelete && (
            <div className='button-with-label'>
              <DefaultButton
                className='circular-button'
                title={t('authoring.Delete')}
                ariaLabel={t('authoring.Delete')}
                styles={{
                  ...buttonStyles,
                  root: {
                    ...buttonStyles.root,
                    backgroundColor: selectedCount === 0 || hasOthersMessages ? 'transparent' : '#d32f2f',
                    ...((selectedCount === 0 || hasOthersMessages) && {
                      opacity: '0.5',
                      selectors: {
                        '&.ms-Button:disabled': {
                          backgroundColor: 'transparent',
                          cursor: 'not-allowed'
                        }
                      }
                    })
                  },
                  rootHovered: {
                    backgroundColor: selectedCount === 0 || hasOthersMessages ? 'transparent' : '#b71c1c'
                  }
                }}
                onClick={onBatchDelete}
                disabled={selectedCount === 0 || hasOthersMessages}
                onMouseEnter={() => setIsDeleteHovered(true)}
                onMouseLeave={() => setIsDeleteHovered(false)}
              >
                <img
                  src={isDeleteHovered && selectedCount > 0 && !hasOthersMessages ? DeleteHoverIcon : DeleteIcon}
                  alt='Delete'
                  className='normal'
                  style={iconStyle}
                />
              </DefaultButton>
              <span style={{ opacity: selectedCount === 0 || hasOthersMessages ? 0.5 : 1 }}>
                {t('authoring.Delete')}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='panel-header idigest-chat-header'>
      {showBack && onBack && (
        <div className='chat-room-back'>
          <button type='button' onClick={onBack} className='chat-room-back-button' aria-label={t('Back')}>
            <img src={backIcon} alt='' width={18} height={18} className='chat-room-back-icon' />
            <span>{t('Back')}</span>
          </button>
        </div>
      )}
      <div className='header-title chat-header-title'>
        <div title={title || undefined}>{title || '\u00a0'}</div>
        {autoDeleteNotice && <div className='chat-header-auto-delete'>{autoDeleteNotice}</div>}
      </div>
      {normalActions}
    </div>
  );
}
